"use client";
// ===================================================
// GAM Command Center — מחברת (Pinkas) — per-note autosave
// One controller per mounted note. The note id is fixed for the controller's
// whole life, so content can never be written under another note's id — the
// failure the old "save before navigate" flow could only avoid by awaiting.
//
// Order of safety:
//   1. every edit goes to a local draft first (survives refresh / crash)
//   2. a debounced save writes only the fields that changed
//   3. leaving the note (switch, close tab, close panel, unmount) sends what is
//      pending without waiting; the draft is removed only after the server
//      confirmed that exact revision
// Spec: docs/specs/pinkas-unified-workspace-20260924.md §4
// ===================================================
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import type { PinkasPatch } from "@/lib/cc-pinkas-queries";
import {
  clearDraftIfRev,
  effectiveTitle,
  isEditorNormalization,
  readDraft,
  resolveDraft,
  sameContent,
  writeDraft,
} from "@/lib/pinkas/workspace";

export type NoteSaveStatus = "idle" | "dirty" | "saving" | "saved" | "error" | "conflict";

export type SaveNoteFn = (id: string, patch: PinkasPatch) => Promise<{ updated_at: string }>;

interface Options {
  noteId: string;
  draftKey: string;
  server: { title: string; body: JSONContent | null; updatedAt: string };
  save: SaveNoteFn;
  debounceMs?: number;
}

type Field = "title" | "body";

let revSeed = 0;
/** Unique across mounts, so an old controller's late success cannot clear a newer draft. */
function nextRev(): number {
  revSeed += 1;
  return Date.now() * 1000 + (revSeed % 1000);
}

export function useNoteAutosave({ noteId, draftKey, server, save, debounceMs = 800 }: Options) {
  // ---- initial state: server, or a recovered draft -----------------------
  const [init] = useState(() => {
    const resolution = resolveDraft(readDraft(draftKey), server);
    if (resolution.kind === "discard") {
      const d = readDraft(draftKey);
      if (d) clearDraftIfRev(draftKey, d.rev);
    }
    const restored = resolution.kind === "restore" || resolution.kind === "conflict" ? resolution.draft : null;
    const dirty = new Set<Field>();
    if (restored) {
      if (restored.title !== server.title) dirty.add("title");
      if (JSON.stringify(restored.body ?? null) !== JSON.stringify(server.body ?? null)) dirty.add("body");
    }
    return {
      title: restored ? restored.title : server.title,
      body: restored ? restored.body : server.body,
      rev: restored ? restored.rev : 0,
      dirty,
      conflict: resolution.kind === "conflict",
      restored: !!restored,
    };
  });

  const [title, setTitleState] = useState(init.title);
  const [editorContent, setEditorContent] = useState<JSONContent | null>(init.body);
  const [editorKey, setEditorKey] = useState(0);
  const [status, setStatus] = useState<NoteSaveStatus>(
    init.conflict ? "conflict" : init.dirty.size ? "dirty" : "idle",
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [restoredDraft, setRestoredDraft] = useState(init.restored && !init.conflict);

  const latest = useRef({ title: init.title, body: init.body });
  const dirty = useRef<Set<Field>>(init.dirty);
  const rev = useRef(init.rev);
  const base = useRef(server.updatedAt);
  const conflict = useRef(init.conflict);
  const inFlight = useRef(false);
  const again = useRef(false);
  const mounted = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const setStatusSafe = useCallback((s: NoteSaveStatus) => {
    if (mounted.current) setStatus(s);
  }, []);

  // ---- the one write path ------------------------------------------------
  const run = useCallback(async (): Promise<void> => {
    if (conflict.current || dirty.current.size === 0) return;
    if (inFlight.current) {
      again.current = true;
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    if (retryTimer.current) clearTimeout(retryTimer.current);

    const snap = { ...latest.current };
    const fields = new Set(dirty.current);
    const snapRev = rev.current;
    const patch: PinkasPatch = {};
    if (fields.has("title") || !snap.title.trim()) patch.text = effectiveTitle(snap.title, snap.body);
    if (fields.has("body")) patch.body = (snap.body ?? null) as PinkasPatch["body"];

    inFlight.current = true;
    setStatusSafe("saving");
    try {
      const res = await saveRef.current(noteId, patch);
      base.current = res.updated_at;
      if (rev.current === snapRev) {
        dirty.current = new Set();
        clearDraftIfRev(key(), snapRev);
        if (mounted.current) {
          setErrorMessage("");
          setRestoredDraft(false);
        }
        setStatusSafe("saved");
      } else {
        // Newer keystrokes arrived while saving: they stay dirty, but their
        // draft is now based on the version we just wrote.
        for (const f of fields) {
          if (f === "title" ? latest.current.title === snap.title : latest.current.body === snap.body) {
            dirty.current.delete(f);
          }
        }
        const d = readDraft(key());
        if (d) writeDraft(key(), { ...d, baseUpdatedAt: res.updated_at });
      }
    } catch (err) {
      if (mounted.current) setErrorMessage(err instanceof Error ? err.message : "שגיאה לא ידועה");
      setStatusSafe("error");
      // One quiet retry while the note is still open; the draft is safe either way.
      if (mounted.current) retryTimer.current = setTimeout(() => void runRef.current(), 5000);
    } finally {
      inFlight.current = false;
      if (again.current) {
        again.current = false;
        if (dirty.current.size) void runRef.current();
      }
    }
  }, [noteId, setStatusSafe]);

  const runRef = useRef(run);
  runRef.current = run;
  const draftKeyRef = useRef(draftKey);
  function key() {
    return draftKeyRef.current;
  }

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void runRef.current(), debounceMs);
  }, [debounceMs]);

  function record(field: Field) {
    dirty.current.add(field);
    rev.current = nextRev();
    writeDraft(key(), {
      title: latest.current.title,
      body: latest.current.body,
      baseUpdatedAt: base.current,
      rev: rev.current,
    });
    if (!conflict.current) {
      setStatus("dirty");
      schedule();
    }
  }

  const setTitle = useCallback((next: string) => {
    latest.current = { ...latest.current, title: next };
    setTitleState(next);
    record("title");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBody = useCallback((next: JSONContent) => {
    // Loading a doc makes the editor add default attributes and fire a change.
    // Take that as the new baseline instead of an edit — otherwise just opening
    // a note would write a draft and re-save it.
    const untouched = !dirty.current.has("body");
    const normalizedOnly = untouched && isEditorNormalization(latest.current.body, next);
    latest.current = { ...latest.current, body: next };
    if (normalizedOnly) return;
    record("body");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Save immediately (Ctrl/Cmd+S, leaving the note, retry button). */
  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    return runRef.current();
  }, []);

  const resolveConflict = useCallback(
    (keep: "local" | "server", serverNow: { title: string; body: JSONContent | null; updatedAt: string }) => {
      conflict.current = false;
      base.current = serverNow.updatedAt;
      if (keep === "local") {
        setStatus("dirty");
        void runRef.current();
        return;
      }
      clearDraftIfRev(key(), rev.current);
      dirty.current = new Set();
      latest.current = { title: serverNow.title, body: serverNow.body };
      setTitleState(serverNow.title);
      setEditorContent(serverNow.body);
      setEditorKey((k) => k + 1);
      setStatus("idle");
    },
    [],
  );

  // ---- a newer server version while nothing is pending -------------------
  // Claude writes to the same table. When the open note has no local changes,
  // show the new version; when it has, the local draft wins and nothing is
  // replaced under the cursor.
  useEffect(() => {
    if (server.updatedAt === base.current) return;
    if (dirty.current.size || inFlight.current || conflict.current) return;
    base.current = server.updatedAt;
    if (sameContent(latest.current, server)) return;
    latest.current = { title: server.title, body: server.body };
    setTitleState(server.title);
    setEditorContent(server.body);
    setEditorKey((k) => k + 1);
  }, [server]);

  // ---- leaving: flush without waiting ------------------------------------
  useEffect(() => {
    mounted.current = true;
    const onLeave = () => {
      if (dirty.current.size && !conflict.current) void runRef.current();
    };
    const onOnline = () => {
      if (dirty.current.size && !conflict.current) void runRef.current();
    };
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("online", onOnline);
    // A draft recovered on open is saved as soon as the note is shown.
    if (dirty.current.size && !conflict.current) schedule();
    return () => {
      window.removeEventListener("pagehide", onLeave);
      window.removeEventListener("online", onOnline);
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      onLeave();
    };
  }, [schedule]);

  return {
    title,
    setTitle,
    setBody,
    flush,
    status,
    errorMessage,
    restoredDraft,
    dismissRestoredNotice: () => setRestoredDraft(false),
    resolveConflict,
    /** Initial content for the editor. Stable until `editorKey` changes. */
    editorContent,
    editorKey,
  };
}
